import React, { useContext, useState, useEffect } from "react";
import { Grid, IconButton, Icon, Tooltip, ClickAwayListener } from "@mui/material";
import MDBox from "components/MDBox";
import { SidebarContext } from "./sidebarContext";

export enum DefinedButtons {
  ProposalCollapseProducts = "proposal.collapse_products",
  ProposalSummaryView = "proposal.summary_view",
  ProposalPreview = "proposal.preview",
}

enum DefinedButtonsTitles {
  ProposalCollapseProducts = "Collapse All Products",
  ProposalSummaryView = "Proposal Summary View",
  ProposalPreview = "Preview Proposal",
}

enum DefinedButtonsIcons {
  ProposalCollapseProducts = "unfold_less_icon",
  ProposalSummaryView = "fact_check_icon",
  ProposalPreview = "plagiarism_icon",
}

interface SidebarProps {
  buttons?: DefinedButtons[];
}

const Sidebar: React.FC<SidebarProps> = ({ buttons }) => {
  const { clickedButtonHandle, disabledButtons } = useContext(SidebarContext);

  const setScrollUp = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const setScrollDown = () => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleButtonClick = (buttonType: DefinedButtons) => {
    const buttonConfig = disabledButtons[buttonType];
    if (buttonConfig?.disabled) {
      return;
    }
    clickedButtonHandle(buttonType);
  };

  const findTitle = (buttonValue) => {
    const key = Object.keys(DefinedButtons).find(
      (key) => DefinedButtons[key as keyof typeof DefinedButtons] === buttonValue
    ) as DefinedButtons | undefined;
    return DefinedButtonsTitles[key];
  };

  const findIcon = (buttonValue) => {
    const key = Object.keys(DefinedButtons).find(
      (key) => DefinedButtons[key as keyof typeof DefinedButtons] === buttonValue
    ) as DefinedButtons | undefined;
    return DefinedButtonsIcons[key];
  };

  const getButtonTooltip = (button: DefinedButtons) => {
    const buttonConfig = disabledButtons[button];
    if (buttonConfig?.disabled && buttonConfig?.tooltip) {
      return buttonConfig.tooltip;
    }
    return findTitle(button);
  };

  const isButtonDisabled = (button: DefinedButtons) => {
    return disabledButtons[button]?.disabled ?? false;
  };

  return (
    <Grid className="float-sidebar">
      <MDBox sx={{ width: "46px", height: "46px" }}>
        <Tooltip title="Scroll to Top" placement="left">
          <IconButton onClick={() => setScrollUp()} sx={{ color: "#ffffff" }}>
            <Icon sx={{ fontWeight: "bolder" }}>north_icon</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
      {buttons.map((button) => (
        <SidebarButton
          key={button}
          button={button}
          isDisabled={isButtonDisabled(button)}
          tooltip={getButtonTooltip(button)}
          icon={findIcon(button)}
          onClick={() => handleButtonClick(button)}
        />
      ))}
      <MDBox sx={{ width: "46px", height: "46px" }}>
        <Tooltip title="Scroll to Bottom" placement="left">
          <IconButton onClick={() => setScrollDown()} sx={{ color: "#ffffff" }}>
            <Icon sx={{ fontWeight: "bolder" }}>south_icon</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    </Grid>
  );
};

interface SidebarButtonProps {
  button: DefinedButtons;
  isDisabled: boolean;
  tooltip: string;
  icon: string;
  onClick: () => void;
}

const SidebarButton: React.FC<SidebarButtonProps> = ({
  button,
  isDisabled,
  tooltip,
  icon,
  onClick,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [wasClickedWhileDisabled, setWasClickedWhileDisabled] = useState(false);

  useEffect(() => {
    if (!isDisabled) {
      setTooltipOpen(false);
      setWasClickedWhileDisabled(false);
    }
  }, [isDisabled]);

  const handleClick = () => {
    if (isDisabled) {
      setWasClickedWhileDisabled(true);
      setTooltipOpen(true);
    } else {
      onClick();
    }
  };

  const handleClickAway = () => {
    if (wasClickedWhileDisabled) {
      setTooltipOpen(false);
      setWasClickedWhileDisabled(false);
    }
  };

  const handleMouseEnter = () => {
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    if (!wasClickedWhileDisabled) {
      setTooltipOpen(false);
    }
  };

  return (
    <MDBox sx={{ width: "46px", height: "46px", alignItems: "center", display: "flex" }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <Tooltip title={tooltip} placement="left" open={tooltipOpen} disableHoverListener>
            <span>
              <IconButton
                onClick={handleClick}
                sx={{
                  color: "#ffffff",
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.5)",
                  },
                }}
                disabled={isDisabled}
              >
                <Icon sx={{ fontWeight: "bolder" }}>{icon}</Icon>
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </ClickAwayListener>
    </MDBox>
  );
};

export default Sidebar;
